import { makePersisted } from "@solid-primitives/storage";
import { createContext, useContext } from "solid-js";
import { createSignal } from "solid-js";
import { createStore } from "solid-js/store";
import { isServer } from "solid-js/web";

// Artifact types based on content
export type ArtifactType = "code" | "image" | "document" | "data" | "chart" | "diagram" | "audio" | "other";

// Individual artifact state
export interface ArtifactState {
  id: string;
  title: string;
  type: ArtifactType;
  content: any; // The actual artifact content
  messageId: string; // Reference to the message that created this artifact
  operationId: string; // Reference to the operation
  blockId: string; // Reference to the specific block
  createdAt: Date;
  updatedAt: Date;
  isProcessing: boolean;
  metadata?: Record<string, any>; // Additional metadata like file extensions, sizes, etc.
}

// Overall artifact store state
export interface ArtifactStoreState {
  artifacts: ArtifactState[];
  activeArtifactId?: string;
  isPanelVisible: boolean;
  panelWidth: number;
}

// Store actions
export interface ArtifactStoreActions {
  addArtifact: (artifact: Omit<ArtifactState, "id" | "createdAt" | "updatedAt">) => string;
  updateArtifact: (id: string, updates: Partial<ArtifactState>) => void;
  removeArtifact: (id: string) => void;
  setActiveArtifact: (id: string | undefined) => void;
  showPanel: () => void;
  hidePanel: () => void;
  togglePanel: () => void;
  setPanelWidth: (width: number) => void;
  clearAll: () => void;
  getArtifact: (id: string) => ArtifactState | undefined;
  getArtifactsByOperation: (operationId: string) => ArtifactState[];
}

// Generate unique IDs for artifacts
const generateArtifactId = () => `artifact_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Detect artifact type from content and metadata
const detectArtifactType = (content: any, metadata?: Record<string, any>): ArtifactType => {
  if (metadata?.mimeType) {
    if (metadata.mimeType.startsWith("image/")) return "image";
    if (metadata.mimeType.startsWith("audio/")) return "audio";
    if (metadata.mimeType.includes("json")) return "data";
    if (metadata.mimeType.includes("csv")) return "data";
  }

  if (metadata?.language || metadata?.fileExtension) {
    const ext = metadata.fileExtension?.toLowerCase();
    const lang = metadata.language?.toLowerCase();
    if (ext && ["js", "ts", "py", "java", "cpp", "c", "html", "css"].includes(ext)) return "code";
    if (lang && ["javascript", "typescript", "python", "java", "cpp"].includes(lang)) return "code";
  }

  if (typeof content === "string") {
    // Simple heuristics for content detection
    if (content.includes("```") || content.includes("function") || content.includes("class ")) return "code";
    if (content.length > 1000) return "document";
  }

  return "other";
};

// Create the store
const createArtifactStore = () => {
  // Panel visibility - not persisted, always starts hidden
  const [isPanelVisible, setIsPanelVisible] = createSignal(false);

  const [panelWidth, setPanelWidth] = isServer
    ? createSignal(400)
    : makePersisted(createSignal(400), {
        name: "artifact_panel_width",
        storage: localStorage,
      });

  // Main store for artifacts and active state
  const [store, setStore] = createStore<{
    artifacts: ArtifactState[];
    activeArtifactId?: string;
  }>({
    artifacts: [],
    activeArtifactId: undefined,
  });

  // Actions
  const actions: ArtifactStoreActions = {
    addArtifact: (artifactData) => {
      const id = generateArtifactId();
      const now = new Date();

      const artifact: ArtifactState = {
        ...artifactData,
        id,
        createdAt: now,
        updatedAt: now,
        type: artifactData.type || detectArtifactType(artifactData.content, artifactData.metadata),
      };

      const wasEmpty = store.artifacts.length === 0;

      setStore("artifacts", (prev) => [...prev, artifact]);

      // Auto-set as active if it's the first artifact or no artifact is currently active
      if (wasEmpty || !store.activeArtifactId) {
        setStore("activeArtifactId", id);
      }

      // Auto-show panel when first artifact is added
      if (wasEmpty) {
        setIsPanelVisible(true);
      }

      return id;
    },

    updateArtifact: (id, updates) => {
      setStore("artifacts", (artifact) => artifact.id === id, {
        ...updates,
        updatedAt: new Date(),
      });
    },

    removeArtifact: (id) => {
      // If removing the active artifact, switch to another one
      if (store.activeArtifactId === id) {
        const remainingArtifacts = store.artifacts.filter((a) => a.id !== id);
        setStore("activeArtifactId", remainingArtifacts.length > 0 ? remainingArtifacts[0].id : undefined);
      }

      setStore("artifacts", (prev) => prev.filter((a) => a.id !== id));

      // Hide panel if no artifacts remain
      if (store.artifacts.length <= 1) {
        setIsPanelVisible(false);
      }
    },

    setActiveArtifact: (id) => {
      setStore("activeArtifactId", id);
    },

    showPanel: () => {
      setIsPanelVisible(true);
    },

    hidePanel: () => {
      setIsPanelVisible(false);
    },

    togglePanel: () => {
      setIsPanelVisible(!isPanelVisible());
    },

    setPanelWidth: (width) => {
      setPanelWidth(width);
    },

    clearAll: () => {
      setStore("artifacts", []);
      setStore("activeArtifactId", undefined);
      setIsPanelVisible(false);
    },

    getArtifact: (id) => {
      return store.artifacts.find((a) => a.id === id);
    },

    getArtifactsByOperation: (operationId) => {
      return store.artifacts.filter((a) => a.operationId === operationId);
    },
  };

  // Combined state accessor
  const state = () => ({
    artifacts: store.artifacts,
    activeArtifactId: store.activeArtifactId,
    isPanelVisible: isPanelVisible(),
    panelWidth: panelWidth(),
  });

  return [state, actions] as const;
};

// Context for providing the store
const ArtifactStoreContext = createContext<ReturnType<typeof createArtifactStore>>();

// Provider component
export const ArtifactStoreProvider = (props: { children: any }) => {
  const store = createArtifactStore();

  return <ArtifactStoreContext.Provider value={store}>{props.children}</ArtifactStoreContext.Provider>;
};

// Hook to use the store
export const useArtifactStore = () => {
  const context = useContext(ArtifactStoreContext);
  if (!context) {
    throw new Error("useArtifactStore must be used within an ArtifactStoreProvider");
  }
  return context;
};
